import { useState, useEffect, useMemo } from "react"
import { format } from "date-fns"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Progress } from "@/components/ui/progress"
import { Separator } from "@/components/ui/separator"
import { 
  Plus,
  Search,
  Filter,
  Eye,
  MessageSquare,
  FileText,
  Bot,
  Sparkles,
  AlertCircle,
  CheckCircle2,
  Clock,
  TrendingUp,
  TrendingDown,
  Activity,
  PoundSterling,
  Zap,
  X,
  Settings,
  Loader2,
  Download,
  User,
  Building2,
  Mail,
  Phone,
  AlertTriangle,
  DollarSign,
  UserCheck,
  GitBranch,
  Pencil,
  Receipt,
  Trash2,
} from "lucide-react"
import { ColumnConfigDialog, type ColumnDefinition } from "@/components/shared/column-config-dialog"
import { useScreenColumnPreferences } from "@/hooks/useScreenColumnPreferences"
import { useDisputesList, useCreateDispute, useUpdateDispute, useCustomerOptions, useDisputeTypeOptions, useUserOptions } from "@/hooks/useDisputesData"
import type { DisputeUI } from "@/types/ui/disputes"
import type { CreateDisputeRequest, UpdateDisputeRequest } from "@/types/api/disputes"
import { FeatureFeedbackCollector } from "@/components/FeedbackCollector"
import { LogCallModal, SendEmailModal } from "@/components/collections/action-modals"
import { exportDisputesToExcel } from "@/lib/export-utils"
import { cn } from "@/lib/utils"
import { useUsersList } from "@/hooks/useUsersData"
import { useCustomerContactsList } from "@/hooks/useCustomerContacts"
import { getWorkflowForDisputeType } from "@/lib/utils/dispute-workflow-mapper"
import { ConsolidatedActivityTimeline } from "@/components/collections/consolidated-activity-timeline"
import type { CollectionActivity, CollectionCommunication, CaseNote } from "@/types/collections"

const DEFAULT_DISPUTE_COLUMNS: ColumnDefinition[] = [
  { id: 'disputeId', label: 'Dispute ID', visible: true, order: 0 },
  { id: 'customer', label: 'Customer', visible: true, order: 1 },
  { id: 'type', label: 'Type', visible: true, order: 2 },
  { id: 'amount', label: 'Amount', visible: true, order: 3 },
  { id: 'status', label: 'Status', visible: true, order: 4 },
  { id: 'priority', label: 'Priority', visible: true, order: 5 },
  { id: 'workflowProgress', label: 'Workflow Progress', visible: true, order: 6 },
  { id: 'aiScore', label: 'AI Score', visible: true, order: 7 },
  { id: 'assignedTo', label: 'Assigned To', visible: true, order: 8 },
  { id: 'dueDate', label: 'Due Date', visible: true, order: 9 },
  { id: 'createdDate', label: 'Created Date', visible: false, order: 10 },
  { id: 'workflow', label: 'Workflow', visible: false, order: 11 },
  { id: 'description', label: 'Description', visible: false, order: 12 },
];

const getTransactionStatusVariant = (status: string) => {
  if (status === 'resolved') return 'default' as const;
  if (status === 'disputed') return 'destructive' as const;
  return 'secondary' as const;
};

const getContactPlaceholder = (customerId: string, contactCount: number) => {
  if (!customerId) return "Select customer first";
  if (contactCount > 0) return "Select contact";
  return "No contacts available";
};

const statusColors = {
  "New": "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300",
  "Under Review": "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300",
  "In Progress": "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300",
  "Escalated": "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300",
  "Resolved": "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300",
  "Closed": "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300",
}

const priorityColors = {
  "Low": "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300",
  "Medium": "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300",
  "High": "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300",
  "Critical": "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300",
}

// Mock data for Activity Timeline
const mockActivities: CollectionActivity[] = [
  {
    id: "act-1",
    caseId: "CASE-001",
    type: "status_change",
    description: "Dispute status changed from 'New' to 'In Progress'",
    outcome: "Status updated successfully",
    createdBy: "System",
    createdAt: new Date("2024-01-15T09:00:00"),
  },
  {
    id: "act-2",
    caseId: "CASE-001",
    type: "call",
    description: "Called customer to discuss dispute details",
    outcome: "Customer provided additional documentation",
    createdBy: "John Smith",
    createdAt: new Date("2024-01-16T10:30:00"),
  },
]

const mockCommunications: CollectionCommunication[] = [
  {
    id: "comm-1",
    caseId: "CASE-001",
    type: "email",
    direction: "outbound",
    subject: "Dispute Investigation - Additional Information Required",
    content: "We are investigating your dispute and require additional documentation to proceed.",
    sentBy: "Sarah Johnson",
    sentAt: new Date("2024-01-15T14:15:00"),
    status: "sent",
  },
  {
    id: "comm-2",
    caseId: "CASE-001",
    type: "email",
    direction: "inbound",
    subject: "RE: Dispute Investigation",
    content: "Please find attached the requested documentation for your review.",
    sentBy: "Customer",
    sentAt: new Date("2024-01-16T09:00:00"),
    status: "delivered",
  },
]

const mockNotes: CaseNote[] = [
  {
    id: "note-1",
    caseId: "CASE-001",
    content: "Contacted customer to verify service dates. Awaiting response.",
    createdBy: "John Smith",
    createdAt: new Date("2024-01-16T10:30:00"),
  },
  {
    id: "note-2",
    caseId: "CASE-001",
    content: "Dispute automatically categorized as 'Billing Error' with 85% confidence.",
    createdBy: "AI Agent",
    createdAt: new Date("2024-01-15T14:15:00"),
  },
]

const documents = [
  { name: "Original Invoice.pdf", size: "245 KB", date: "2024-01-15" },
  { name: "Service Agreement.pdf", size: "1.2 MB", date: "2024-01-15" },
  { name: "Customer Email.msg", size: "45 KB", date: "2024-01-16" },
]

const initialCreateFormState = {
  customerId: "",
  contactId: "",
  contactEmail: "",
  contactPhone: "",
  disputeTypeId: "",
  amount: "",
  statusId: "1",
  priorityId: "",
  assignedToUserId: "",
  dueDate: "",
  description: "",
}

const filterDisputes = (
  disputes: DisputeUI[],
  searchQuery: string,
  statusFilter: string,
  priorityFilter: string
) => {
  return disputes.filter((dispute) => {
    const matchesSearch = 
      dispute.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
      dispute.customer.toLowerCase().includes(searchQuery.toLowerCase()) ||
      dispute.type.toLowerCase().includes(searchQuery.toLowerCase())
    
    const matchesStatus = statusFilter === "all" || dispute.status === statusFilter
    const matchesPriority = priorityFilter === "all" || dispute.priority === priorityFilter

    return matchesSearch && matchesStatus && matchesPriority
  })
}

export default function DisputesAllPage() {
  console.log('[DisputesAllPage] Function called');
  
  const [searchQuery, setSearchQuery] = useState("")
  const [statusFilter, setStatusFilter] = useState("all")
  const [priorityFilter, setPriorityFilter] = useState("all")
  const [selectedDispute, setSelectedDispute] = useState<DisputeUI | null>(null)
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false)
  const [isCreateDrawerOpen, setIsCreateDrawerOpen] = useState(false)
  const [selectedDisputeId, setSelectedDisputeId] = useState<string | null>(null)
  const [isColumnConfigOpen, setIsColumnConfigOpen] = useState(false)
  const [logCallOpen, setLogCallOpen] = useState(false)
  const [sendEmailOpen, setSendEmailOpen] = useState(false)
  const [isEditMode, setIsEditMode] = useState(false)
  const [createForm, setCreateForm] = useState(initialCreateFormState)
  const [createFormContacts, setCreateFormContacts] = useState<Array<{ id: string; name: string; email: string; phone: string }>>([])
  
  // Next Action Modal state
  const [isNextActionModalOpen, setIsNextActionModalOpen] = useState(false)
  const [nextActionNotes, setNextActionNotes] = useState('')
  const [nextActionFollowUpDate, setNextActionFollowUpDate] = useState('')
  
  // Edit mode state for dispute fields
  const [editedDispute, setEditedDispute] = useState<Partial<DisputeUI>>({})
  const [customerContacts, setCustomerContacts] = useState<Array<{ id: string; name: string; email: string; phone: string }>>([])
  
  // Transaction state
  const [editedTransactions, setEditedTransactions] = useState<Array<{
    id: string
    transactionId: string
    date: string
    description: string
    amount: number
    status: 'disputed' | 'resolved' | 'pending'
  }>>([])
  
  console.log('[DisputesAllPage] About to call useScreenColumnPreferences');
  const { 
    columns: disputeColumns,
    visibleColumns, 
    savePreferences: saveDisputeColumnPreferences,
    resetToDefaults: resetDisputeColumnPreferences,
  } = useScreenColumnPreferences('Dispute_Management_All_Disputes', DEFAULT_DISPUTE_COLUMNS)
  console.log('[DisputesAllPage] useScreenColumnPreferences completed');

  // Mode-aware contacts queries for selected dispute and create form
  const customerId = useMemo(() => selectedDispute?.customerId?.toString() || "0", [selectedDispute?.customerId])
  const { data: selectedDisputeContacts = [] } = useCustomerContactsList(customerId)
  
  const createFormCustomerId = useMemo(() => createForm.customerId || "0", [createForm.customerId])
  const { data: createFormContactsData = [] } = useCustomerContactsList(createFormCustomerId)

  console.log('[DisputesAllPage] About to call useDisputesList');
  const { data: disputes = [], isLoading, refetch: refetchDisputes } = useDisputesList()
  console.log('[DisputesAllPage] useDisputesList completed, disputes:', disputes);
  
  const { data: users = [] } = useUsersList()
  const createDisputeMutation = useCreateDispute()
  const { data: customerOptions = [] } = useCustomerOptions()
  const { data: disputeTypeOptions = [] } = useDisputeTypeOptions()
  const { data: userOptions = [] } = useUserOptions()
  
  const selectedDisputeNumericId = selectedDispute?.numericId ?? 0
  const updateDisputeMutation = useUpdateDispute(selectedDisputeNumericId)

  // Add error boundary logging
  useEffect(() => {
    console.log('[DisputesAllPage] Component mounted');
    console.log('[DisputesAllPage] Disputes:', disputes);
    console.log('[DisputesAllPage] isLoading:', isLoading);
  }, [disputes, isLoading])

  const [editForm, setEditForm] = useState({
    statusId: "",
    priorityId: "",
    assignedToUserId: "",
    dueDate: "",
    description: "",
    resolutionNotes: "",
    latestNote: "",
  })

  // Sync customer contacts when dispute is selected
  useEffect(() => {
    if (selectedDispute && selectedDisputeContacts.length > 0) {
      const newContacts = selectedDisputeContacts.map(c => ({
        id: c.id,
        name: `${c.firstName} ${c.surname}`,
        email: c.email,
        phone: c.phone
      }))
      setCustomerContacts(newContacts)
    }
  }, [selectedDispute?.id])

  // Initialize edited dispute when entering edit mode
  useEffect(() => {
    if (isEditMode && selectedDispute) {
      setEditedDispute({
        contactName: selectedDispute.contactName,
        contactEmail: selectedDispute.contactEmail,
        contactPhone: selectedDispute.contactPhone,
        status: selectedDispute.status,
        priority: selectedDispute.priority,
        assignedTo: selectedDispute.assignedTo,
      })
      // Initialize transactions
      setEditedTransactions(selectedDispute.transactions || [])
    } else {
      setEditedDispute({})
      setEditedTransactions([])
    }
  }, [isEditMode, selectedDispute])

  const handleCreateFormChange = (field: string, value: string) => {
    setCreateForm(prev => ({ ...prev, [field]: value }))
    
    // Auto-populate email and phone when contact is selected
    if (field === "contactId" && value) {
      const selectedContact = createFormContacts.find(c => c.id === value)
      if (selectedContact) {
        setCreateForm(prev => ({
          ...prev,
          contactEmail: selectedContact.email,
          contactPhone: selectedContact.phone
        }))
      }
    }
    
    // Reset contact fields when customer changes
    if (field === "customerId") {
      setCreateForm(prev => ({ ...prev, contactId: "", contactEmail: "", contactPhone: "" }))
      setCreateFormContacts([])
    }
  }
  
  // Sync contacts when customer is selected in create form
  useEffect(() => {
    if (createForm.customerId && createFormContactsData.length > 0) {
      const newContacts = createFormContactsData.map(c => ({
        id: c.id,
        name: `${c.firstName} ${c.surname}`,
        email: c.email,
        phone: c.phone
      }))
      setCreateFormContacts(newContacts)
    } else if (!createForm.customerId) {
      setCreateFormContacts([])
    }
  }, [createForm.customerId])

  const handleEditFormChange = (field: string, value: string) => {
    setEditForm(prev => ({ ...prev, [field]: value }))
  }

  const handleUpdateDispute = async () => {
    if (!selectedDispute) return

    const request: UpdateDisputeRequest = {}
    if (editForm.statusId) request.statusId = parseInt(editForm.statusId)
    if (editForm.priorityId) request.priorityId = parseInt(editForm.priorityId)
    if (editForm.assignedToUserId) request.assignedToUserId = parseInt(editForm.assignedToUserId)
    if (editForm.dueDate) request.dueDate = editForm.dueDate
    if (editForm.description) request.description = editForm.description
    if (editForm.resolutionNotes) request.resolutionNotes = editForm.resolutionNotes
    
    // If latestNote is provided, it will be added as a new note in the Activity tab
    // In a real implementation, this would be sent to the backend to create a new note
    if (editForm.latestNote) {
      console.log("Adding new note to Activity tab:", editForm.latestNote)
    }

    try {
      await updateDisputeMutation.mutateAsync(request)
      setIsViewDialogOpen(false)
      setEditForm({
        statusId: "",
        priorityId: "",
        assignedToUserId: "",
        dueDate: "",
        description: "",
        resolutionNotes: "",
        latestNote: "",
      })
      refetchDisputes()
    } catch (error) {
      console.error("Failed to update dispute:", error)
    }
  }

  const handleCreateDispute = async () => {
    if (!createForm.customerId || !createForm.disputeTypeId || !createForm.amount || !createForm.priorityId) {
      return
    }

    const request: CreateDisputeRequest = {
      customerId: parseInt(createForm.customerId),
      disputeTypeId: parseInt(createForm.disputeTypeId),
      amount: parseFloat(createForm.amount),
      statusId: parseInt(createForm.statusId),
      priorityId: parseInt(createForm.priorityId),
      assignedToUserId: createForm.assignedToUserId ? parseInt(createForm.assignedToUserId) : undefined,
      dueDate: createForm.dueDate || undefined,
      description: createForm.description || undefined,
    }

    try {
      await createDisputeMutation.mutateAsync(request)
      setIsCreateDrawerOpen(false)
      setCreateForm(initialCreateFormState)
      setCreateFormContacts([])
      refetchDisputes()
    } catch (error) {
      console.error("Failed to create dispute:", error)
    }
  }

  const renderColumnHeader = (columnId: string) => {
    const headerMap: Record<string, string> = {
      disputeId: "Dispute ID",
      customer: "Customer",
      type: "Type",
      amount: "Amount",
      status: "Status",
      priority: "Priority",
      workflowProgress: "Workflow Progress",
      aiScore: "AI Score",
      assignedTo: "Assigned To",
      dueDate: "Due Date",
      createdDate: "Created Date",
      workflow: "Workflow",
      description: "Description",
    }
    return headerMap[columnId] || columnId
  }

  const renderColumnCell = (columnId: string, dispute: DisputeUI) => {
    switch (columnId) {
      case "disputeId":
        return (
          <button
            onClick={() => handleViewDispute(dispute)}
            className="font-medium text-primary hover:underline"
          >
            {dispute.id}
          </button>
        )
      case "customer":
        return dispute.customer
      case "type":
        return dispute.type
      case "amount":
        return `£${dispute.amount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` 
      case "status":
        return (
          <Badge className={statusColors[dispute.status as keyof typeof statusColors]}>
            {dispute.status}
          </Badge>
        )
      case "priority":
        return (
          <Badge className={priorityColors[dispute.priority as keyof typeof priorityColors]}>
            {dispute.priority}
          </Badge>
        )
      case "workflowProgress":
        return (
          <div className="flex items-center gap-2">
            <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2 max-w-[100px]">
              <div
                className="bg-primary h-2 rounded-full"
                style={{ width: `${(dispute.currentLevel / dispute.totalLevels) * 100}%` }}
              />
            </div>
            <span className="text-xs text-muted-foreground">
              {dispute.currentLevel}/{dispute.totalLevels}
            </span>
          </div>
        )
      case "aiScore":
        return dispute.aiScore !== null ? (
          <div className="flex items-center gap-1">
            <Bot className="h-3 w-3 text-purple-500" />
            <span className="text-sm font-medium">{dispute.aiScore}%</span>
          </div>
        ) : (
          <span className="text-muted-foreground">—</span>
        )
      case "assignedTo":
        return dispute.assignedTo
      case "dueDate":
        return dispute.dueDate ? format(new Date(dispute.dueDate), "dd/MM/yyyy") : "—"
      case "createdDate":
        return format(new Date(dispute.createdDate), "MMM dd, yyyy")
      case "workflow":
        return dispute.workflow
      case "description":
        return (
          <span className="truncate max-w-[200px] block" title={dispute.description}>
            {dispute.description}
          </span>
        )
      default:
        return null
    }
  }

  const filteredDisputes = filterDisputes(disputes, searchQuery, statusFilter, priorityFilter)

  const handleViewDispute = (dispute: DisputeUI) => {
    setSelectedDispute(dispute)
    setIsViewDialogOpen(true)
  }

  const handleExportToExcel = () => {
    try {
      exportDisputesToExcel(
        filteredDisputes,
        `disputes-${new Date().toISOString().split("T")[0]}.csv`
      )
    } catch (error) {
      console.error("Error exporting disputes:", error)
    }
  }

  const handleNextAction = () => {
    console.log('handleNextAction called')
    setIsNextActionModalOpen(true)
  }

  const handleProceedToNextStage = () => {
    if (!nextActionNotes.trim()) {
      console.log('Notes are required')
      return
    }

    console.log('Proceeding to next stage with notes:', nextActionNotes)
    
    // Close modal and reset
    setIsNextActionModalOpen(false)
    setNextActionNotes('')
    setNextActionFollowUpDate('')
    
    // Show success message (using console since alerts are blocked)
    console.log('✅ Successfully proceeded to next stage!')
  }

  const handleCancelNextAction = () => {
    setIsNextActionModalOpen(false)
    setNextActionNotes('')
    setNextActionFollowUpDate('')
  }

  const handleSaveEditedDispute = async () => {
    if (!selectedDispute) return

    try {
      // Here you would call the update API with the edited fields
      // For now, we'll just close edit mode and update local state
      
      // Update the selected dispute with edited values including transactions
      const updatedDispute = {
        ...selectedDispute,
        ...editedDispute,
        transactions: editedTransactions,
      }
      
      setSelectedDispute(updatedDispute)
      setIsEditMode(false)
      
      console.log("Saving edited dispute:", {
        ...editedDispute,
        transactions: editedTransactions,
      })
      
      refetchDisputes()
    } catch (error) {
      console.error("Failed to save dispute:", error)
    }
  }

  const updateTransactionField = (index: number, field: string, value: string | number) => {
    const updated = [...editedTransactions]
    ;(updated[index] as Record<string, string | number>)[field] = value
    setEditedTransactions(updated)
  }

  const removeTransaction = (index: number) => {
    setEditedTransactions(editedTransactions.filter((_, i) => i !== index))
  }

  const renderNextActionPanel = () => {
    if (!selectedDispute) return null;
    const workflowConfig = getWorkflowForDisputeType(selectedDispute.type);
    const currentStage = workflowConfig.stages.find(s => s.level === selectedDispute.currentLevel);
    const nextStage = workflowConfig.stages.find(s => s.level === selectedDispute.currentLevel + 1);
    
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Zap className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            Next Action
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {currentStage && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold">{currentStage.title}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Current Stage (Level {currentStage.level} of {workflowConfig.stages.length})
                  </p>
                </div>
                <Badge variant="outline" className="bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800">
                  In Progress
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground">
                {currentStage.description}
              </p>
            </div>
          )}
          
          {nextStage && (
            <div className="pt-4 border-t space-y-3">
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
                  Next Stage
                </p>
                <p className="text-sm font-semibold">{nextStage.title}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {nextStage.description}
                </p>
                {Boolean(nextStage.estimatedDays) && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Estimated: {nextStage.estimatedDays} day{nextStage.estimatedDays !== 1 ? 's' : ''}
                  </p>
                )}
              </div>
              <Button 
                className="w-full gap-2"
                onClick={handleNextAction}
              >
                <CheckCircle2 className="h-4 w-4" />
                Complete {currentStage?.title || 'Current Stage'} & Advance
              </Button>
            </div>
          )}
          
          {!nextStage && selectedDispute.currentLevel === workflowConfig.stages.length && (
            <div className="pt-4 border-t">
              <div className="flex items-center gap-2 text-green-600 dark:text-green-400 mb-2">
                <CheckCircle2 className="h-4 w-4" />
                <p className="text-sm font-semibold">Final Stage Reached</p>
              </div>
              <p className="text-xs text-muted-foreground mb-3">
                All workflow stages have been completed. Ready to close dispute.
              </p>
              <Button 
                className="w-full gap-2 bg-green-600 hover:bg-green-700"
                onClick={() => {
                  console.log("Closing dispute");
                }}
              >
                <CheckCircle2 className="h-4 w-4" />
                Close Dispute
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  const getTransactionList = () => {
    if (!selectedDispute) return [];
    if (isEditMode) return editedTransactions;
    return selectedDispute.transactions || [];
  };
  const transactionList = getTransactionList();

  const totalDisputes = disputes?.length || 0
  const activeDisputes = disputes?.filter(d => 
    d.status === "In Progress" || d.status === "Under Review"
  ).length || 0
  const totalAmount = disputes?.reduce((sum, d) => sum + d.amount, 0) || 0
  const automationRate = 87

  // Early return if there's an error loading
  if (!disputes) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading disputes...</p>
        </div>
      </div>
    )
  }

  console.log('[DisputesAllPage] Rendering with', disputes.length, 'disputes');

  return (
    <div className="space-y-6 p-6" style={{ minHeight: '100vh', backgroundColor: 'var(--background)' }}>
      {/* Header Section */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-3xl font-bold tracking-tight">Dispute Management</h1>
          </div>
          <p className="text-muted-foreground">
            Track, manage, and resolve customer disputes with AI-powered automation
          </p>
        </div>
        <div className="flex items-center gap-2">
          <FeatureFeedbackCollector feature="Disputes" />
          <Button onClick={() => setIsCreateDrawerOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            New Dispute
          </Button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {/* Total Disputes */}
        <Card className="border-l-4 border-l-blue-500">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Disputes</CardTitle>
            <Activity className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalDisputes}</div>
            <div className="flex items-center text-xs text-muted-foreground mt-1">
              <TrendingDown className="mr-1 h-3 w-3 text-green-600" />
              <span className="text-green-600 font-medium">-12%</span>
              <span className="ml-1">from last month</span>
            </div>
          </CardContent>
        </Card>

        {/* Active Disputes */}
        <Card className="border-l-4 border-l-amber-500">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Disputes</CardTitle>
            <Clock className="h-4 w-4 text-amber-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{activeDisputes}</div>
            <p className="text-xs text-muted-foreground mt-1">
              2 new this week
            </p>
          </CardContent>
        </Card>

        {/* Disputed Amount */}
        <Card className="border-l-4 border-l-emerald-500">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Disputed Amount</CardTitle>
            <PoundSterling className="h-4 w-4 text-emerald-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">£{totalAmount.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Across all active disputes
            </p>
          </CardContent>
        </Card>

        {/* AI Automation */}
        <Card className="border-l-4 border-l-purple-500">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">AI Automation</CardTitle>
            <Zap className="h-4 w-4 text-purple-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{automationRate}%</div>
            <div className="flex items-center text-xs text-muted-foreground mt-1">
              <TrendingUp className="mr-1 h-3 w-3 text-green-600" />
              <span className="text-green-600 font-medium">+5%</span>
              <span className="ml-1">improvement</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>All Disputes</CardTitle>
              <CardDescription>
                Comprehensive view of all customer disputes
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search disputes..."
                  className="pl-8 w-[250px]"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              {/* Status Filter */}
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[150px]">
                  <Filter className="mr-2 h-4 w-4" />
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="New">New</SelectItem>
                  <SelectItem value="Under Review">Under Review</SelectItem>
                  <SelectItem value="In Progress">In Progress</SelectItem>
                  <SelectItem value="Escalated">Escalated</SelectItem>
                  <SelectItem value="Resolved">Resolved</SelectItem>
                </SelectContent>
              </Select>

              {/* Priority Filter */}
              <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                <SelectTrigger className="w-[150px]">
                  <Filter className="mr-2 h-4 w-4" />
                  <SelectValue placeholder="Priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Priority</SelectItem>
                  <SelectItem value="Low">Low</SelectItem>
                  <SelectItem value="Medium">Medium</SelectItem>
                  <SelectItem value="High">High</SelectItem>
                  <SelectItem value="Critical">Critical</SelectItem>
                </SelectContent>
              </Select>

              {/* Export to Excel */}
              <Button
                variant="outline"
                size="icon"
                onClick={handleExportToExcel}
                disabled={filteredDisputes.length === 0}
                title="Export to Excel"
              >
                <Download className="h-4 w-4" />
              </Button>

              {/* Column Configuration */}
              <Button
                variant="outline"
                size="icon"
                onClick={() => setIsColumnConfigOpen(true)}
                title="Configure columns"
              >
                <Settings className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[50px]"></TableHead>
                {visibleColumns.map((col) => (
                  <TableHead key={col.id}>{renderColumnHeader(col.id)}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={visibleColumns.length + 1} className="h-64 text-center">
                    <div className="flex flex-col items-center justify-center space-y-4">
                      <Loader2 className="h-8 w-8 animate-spin text-primary" />
                      <p className="text-sm text-muted-foreground">Loading disputes...</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : filteredDisputes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={visibleColumns.length + 1} className="h-64 text-center">
                    <p className="text-sm text-muted-foreground">No disputes found</p>
                  </TableCell>
                </TableRow>
              ) : (
                filteredDisputes.map((dispute) => (
                  <TableRow 
                    key={dispute.id}
                    className={selectedDisputeId === dispute.id ? "bg-muted/50" : ""}
                  >
                    <TableCell>
                      <Checkbox
                        checked={selectedDisputeId === dispute.id}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedDisputeId(dispute.id)
                            setSelectedDispute(dispute)
                          } else {
                            setSelectedDisputeId(null)
                            setSelectedDispute(null)
                          }
                        }}
                        aria-label={`Select dispute ${dispute.id}`}
                      />
                    </TableCell>
                    {visibleColumns.map((col) => (
                      <TableCell key={col.id}>{renderColumnCell(col.id, dispute)}</TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Column Configuration Dialog */}
      <ColumnConfigDialog
        open={isColumnConfigOpen}
        onOpenChange={setIsColumnConfigOpen}
        columns={disputeColumns}
        onApply={saveDisputeColumnPreferences}
        onReset={resetDisputeColumnPreferences}
      />

      {/* View Dispute Sheet */}
      <Sheet open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <SheetContent side="right" className="w-full sm:max-w-3xl overflow-y-auto px-6">
          {selectedDispute && (
            <>
              <SheetHeader className="px-0">
                <div className="flex items-start justify-between">
                  <div className="space-y-1.5">
                    <SheetTitle>Dispute Details - {selectedDispute.id}</SheetTitle>
                    <SheetDescription>
                      View and manage dispute information
                    </SheetDescription>
                  </div>
                  <div className="flex gap-2">
                    {isEditMode && (
                      <Button
                        size="sm"
                        onClick={handleSaveEditedDispute}
                        className="gap-2"
                      >
                        <CheckCircle2 className="h-4 w-4" />
                        Save Changes
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setIsEditMode(!isEditMode)}
                      className="gap-2"
                    >
                      <Pencil className="h-4 w-4" />
                      {isEditMode ? "Cancel" : "Edit Dispute"}
                    </Button>
                  </div>
                </div>
              </SheetHeader>

              <Tabs defaultValue="overview" className="mt-6">
                <TabsList className="grid w-full grid-cols-6">
                  <TabsTrigger value="overview">Overview</TabsTrigger>
                  <TabsTrigger value="transactions">Transactions</TabsTrigger>
                  <TabsTrigger value="workflow">Workflow</TabsTrigger>
                  <TabsTrigger value="activity">Activity</TabsTrigger>
                  <TabsTrigger value="documents">Documents</TabsTrigger>
                  <TabsTrigger value="ai-insights">AI Insights</TabsTrigger>
                </TabsList>

                {/* Tab 1: Overview */}
                <TabsContent value="overview" className="space-y-3">
                  {/* Top Row: Customer Information and Dispute Summary */}
                  <div className="grid gap-3 md:grid-cols-2">
                    {/* Customer Information Panel */}
                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-base flex items-center gap-2">
                          <User className="h-4 w-4 text-primary" />
                          Customer Information
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div className="space-y-1">
                          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                            <Building2 className="h-3 w-3" />
                            Customer
                          </p>
                          <p className="text-sm font-semibold">
                            {selectedDispute.customerAccountCode} - {selectedDispute.customer}
                          </p>
                        </div>
                        <div className="space-y-1">
                          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                            <User className="h-3 w-3" />
                            Contact Name
                          </p>
                          {isEditMode ? (
                            <Select
                              value={editedDispute.contactName || selectedDispute.contactName}
                              onValueChange={(value) => {
                                const contact = customerContacts.find(c => c.name === value)
                                setEditedDispute(prev => ({
                                  ...prev,
                                  contactName: value,
                                  contactEmail: contact?.email || prev.contactEmail,
                                  contactPhone: contact?.phone || prev.contactPhone,
                                }))
                              }}
                            >
                              <SelectTrigger className="h-8">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {customerContacts.map((contact) => (
                                  <SelectItem key={contact.id} value={contact.name}>
                                    {contact.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <p className="text-sm font-semibold">{selectedDispute.contactName}</p>
                          )}
                        </div>
                        <div className="space-y-1">
                          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                            <Mail className="h-3 w-3" />
                            Email
                          </p>
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              onClick={() => setSendEmailOpen(true)}
                              className="gap-2 bg-teal-600 hover:bg-teal-700 text-white h-7 px-2 shrink-0"
                            >
                              <Mail className="h-3.5 w-3.5" />
                            </Button>
                            <p className="text-sm font-semibold break-all">{selectedDispute.contactEmail}</p>
                          </div>
                        </div>
                        <div className="space-y-1">
                          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                            <Phone className="h-3 w-3" />
                            Phone
                          </p>
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              onClick={() => setLogCallOpen(true)}
                              className="gap-2 bg-teal-600 hover:bg-teal-700 text-white h-7 px-2 shrink-0"
                            >
                              <Phone className="h-3.5 w-3.5" />
                            </Button>
                            <p className="text-sm font-semibold">{selectedDispute.contactPhone}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Dispute Summary Panel */}
                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-base flex items-center gap-2">
                          <AlertTriangle className="h-4 w-4 text-orange-600 dark:text-orange-400" />
                          Dispute Summary
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                              <FileText className="h-3 w-3" />
                              Dispute ID
                            </p>
                            <p className="text-sm font-semibold">{selectedDispute.id}</p>
                          </div>
                          <div className="space-y-1">
                            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                              <DollarSign className="h-3 w-3" />
                              Amount
                            </p>
                            <p className="text-base font-bold text-red-600 dark:text-red-400">
                              £{selectedDispute.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </p>
                          </div>
                          <div className="space-y-1">
                            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Dispute Type</p>
                            <p className="text-sm font-semibold">{selectedDispute.type}</p>
                          </div>
                          <div className="space-y-1.5">
                            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Status</p>
                            {isEditMode ? (
                              <Select
                                value={editedDispute.status || selectedDispute.status}
                                onValueChange={(value) => setEditedDispute(prev => ({ ...prev, status: value }))}
                              >
                                <SelectTrigger className="h-8">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="New">New</SelectItem>
                                  <SelectItem value="Under Review">Under Review</SelectItem>
                                  <SelectItem value="In Progress">In Progress</SelectItem>
                                  <SelectItem value="Escalated">Escalated</SelectItem>
                                  <SelectItem value="Resolved">Resolved</SelectItem>
                                  <SelectItem value="Closed">Closed</SelectItem>
                                </SelectContent>
                              </Select>
                            ) : (
                              <Badge className={cn(statusColors[selectedDispute.status as keyof typeof statusColors] || "bg-gray-500", "w-fit")}>
                                {selectedDispute.status}
                              </Badge>
                            )}
                          </div>
                          <div className="space-y-1.5">
                            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Priority</p>
                            {isEditMode ? (
                              <Select
                                value={editedDispute.priority || selectedDispute.priority}
                                onValueChange={(value) => setEditedDispute(prev => ({ ...prev, priority: value }))}
                              >
                                <SelectTrigger className="h-8">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="Low">Low</SelectItem>
                                  <SelectItem value="Medium">Medium</SelectItem>
                                  <SelectItem value="High">High</SelectItem>
                                  <SelectItem value="Critical">Critical</SelectItem>
                                </SelectContent>
                              </Select>
                            ) : (
                              <Badge className={cn(priorityColors[selectedDispute.priority as keyof typeof priorityColors] || "bg-gray-500", "w-fit")}>
                                {selectedDispute.priority}
                              </Badge>
                            )}
                          </div>
                          <div className="space-y-1">
                            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                              <UserCheck className="h-3 w-3" />
                              Assigned To
                            </p>
                            {isEditMode ? (
                              <Select
                                value={editedDispute.assignedTo || selectedDispute.assignedTo}
                                onValueChange={(value) => setEditedDispute(prev => ({ ...prev, assignedTo: value }))}
                              >
                                <SelectTrigger className="h-8">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {users.map((user) => (
                                    <SelectItem key={user.id} value={`${user.firstName} ${user.lastName}`}>
                                      {`${user.firstName} ${user.lastName}`}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : (
                              <p className="text-sm font-semibold">{selectedDispute.assignedTo}</p>
                            )}
                          </div>
                          <div className="space-y-1.5 col-span-2">
                            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                              <GitBranch className="h-3 w-3" />
                              Workflow Level
                            </p>
                            <div className="space-y-1.5">
                              <div className="flex items-center justify-between text-xs">
                                <span className="font-semibold">{selectedDispute.workflow}</span>
                                <span className="text-muted-foreground">
                                  Level {selectedDispute.currentLevel} of {selectedDispute.totalLevels}
                                </span>
                              </div>
                              <Progress 
                                value={(selectedDispute.currentLevel / selectedDispute.totalLevels) * 100} 
                                className="h-2" 
                              />
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {renderNextActionPanel()}

                  {/* Dispute Notes Panel */}
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center gap-2">
                        <MessageSquare className="h-4 w-4 text-green-600 dark:text-green-400" />
                        Dispute Notes
                      </CardTitle>
                      <CardDescription>
                        {isEditMode ? "Edit the latest note or add a new one" : "Latest note from activity timeline"}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {mockNotes.length > 0 && (
                        <div className="space-y-2">
                          {isEditMode ? (
                            <div className="space-y-2">
                              <Textarea 
                                placeholder="Add or edit dispute note..." 
                                rows={4}
                                className="text-sm"
                                value={editForm.latestNote || mockNotes[0].content}
                                onChange={(e) => handleEditFormChange("latestNote", e.target.value)}
                              />
                              <p className="text-xs text-muted-foreground">
                                Saving will update the Activity tab with a new note entry
                              </p>
                            </div>
                          ) : (
                            <>
                              <p className="text-sm text-muted-foreground whitespace-pre-wrap leading-relaxed">
                                {mockNotes[0].content}
                              </p>
                              <div className="flex items-center gap-2 text-xs text-muted-foreground pt-2 border-t">
                                <User className="h-3 w-3" />
                                <span>{mockNotes[0].createdBy}</span>
                                <span>•</span>
                                <Clock className="h-3 w-3" />
                                <span>{format(mockNotes[0].createdAt, "MMM dd, yyyy 'at' h:mm a")}</span>
                              </div>
                            </>
                          )}
                        </div>
                      )}
                      {mockNotes.length === 0 && (
                        <div className="text-center py-6">
                          <MessageSquare className="h-8 w-8 text-muted-foreground mx-auto mb-2 opacity-50" />
                          <p className="text-sm text-muted-foreground">No notes yet</p>
                          {isEditMode && (
                            <p className="text-xs text-muted-foreground mt-1">Add the first note above</p>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Tab 2: Transactions */}
                <TabsContent value="transactions" className="space-y-4">
                  <Card>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle className="text-base flex items-center gap-2">
                            <Receipt className="h-4 w-4 text-primary" />
                            Transactions Under Dispute
                          </CardTitle>
                          <CardDescription>
                            {isEditMode ? 'Add, edit, or remove transactions' : 'View transactions associated with this dispute'}
                          </CardDescription>
                        </div>
                        {isEditMode && (
                          <Button
                            size="sm"
                            onClick={() => {
                              const newTransaction = {
                                id: `temp-${Date.now()}`,
                                transactionId: '',
                                date: new Date().toISOString().split('T')[0],
                                description: '',
                                amount: 0,
                                status: 'disputed' as const,
                              }
                              setEditedTransactions([...editedTransactions, newTransaction])
                            }}
                            className="gap-2"
                          >
                            <Plus className="h-4 w-4" />
                            Add Transaction
                          </Button>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent>
                      {transactionList.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground">
                          <Receipt className="h-12 w-12 mx-auto mb-3 opacity-50" />
                          <p className="font-medium">No transactions found</p>
                          {isEditMode && (
                            <p className="text-sm mt-2">Click &quot;Add Transaction&quot; to add a transaction to this dispute</p>
                          )}
                        </div>
                      ) : (
                        <div className="space-y-4">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Transaction ID</TableHead>
                                <TableHead>Date</TableHead>
                                <TableHead>Description</TableHead>
                                <TableHead className="text-right">Amount</TableHead>
                                <TableHead>Status</TableHead>
                                {isEditMode && <TableHead className="w-[50px]"></TableHead>}
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {transactionList.map((transaction, index) => (
                                <TableRow key={transaction.id}>
                                  <TableCell>
                                    {isEditMode ? (
                                      <Input
                                        value={transaction.transactionId}
                                        onChange={(e) => updateTransactionField(index, 'transactionId', e.target.value)}
                                        placeholder="TXN-001"
                                        className="h-8"
                                      />
                                    ) : (
                                      <span className="font-mono text-sm">{transaction.transactionId}</span>
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    {isEditMode ? (
                                      <Input
                                        type="date"
                                        value={transaction.date}
                                        onChange={(e) => updateTransactionField(index, 'date', e.target.value)}
                                        className="h-8"
                                      />
                                    ) : (
                                      format(new Date(transaction.date), 'dd MMM yyyy')
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    {isEditMode ? (
                                      <Input
                                        value={transaction.description}
                                        onChange={(e) => updateTransactionField(index, 'description', e.target.value)}
                                        placeholder="Transaction description"
                                        className="h-8"
                                      />
                                    ) : (
                                      transaction.description
                                    )}
                                  </TableCell>
                                  <TableCell className="text-right">
                                    {isEditMode ? (
                                      <Input
                                        type="number"
                                        step="0.01"
                                        value={transaction.amount}
                                        onChange={(e) => updateTransactionField(index, 'amount', parseFloat(e.target.value) || 0)}
                                        className="h-8 text-right"
                                      />
                                    ) : (
                                      <span className="font-semibold">
                                        £{transaction.amount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                      </span>
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    {isEditMode ? (
                                      <Select
                                        value={transaction.status}
                                        onValueChange={(value: 'disputed' | 'resolved' | 'pending') => updateTransactionField(index, 'status', value)}
                                      >
                                        <SelectTrigger className="h-8">
                                          <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="disputed">Disputed</SelectItem>
                                          <SelectItem value="pending">Pending</SelectItem>
                                          <SelectItem value="resolved">Resolved</SelectItem>
                                        </SelectContent>
                                      </Select>
                                    ) : (
                                      <Badge variant={getTransactionStatusVariant(transaction.status)}>
                                        {transaction.status}
                                      </Badge>
                                    )}
                                  </TableCell>
                                  {isEditMode && (
                                    <TableCell>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => removeTransaction(index)}
                                        className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </TableCell>
                                  )}
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                          
                          <div className="flex justify-end pt-4 border-t">
                            <div className="text-right space-y-1">
                              <p className="text-sm text-muted-foreground">Total Disputed Amount</p>
                              <p className="text-2xl font-bold text-primary">
                                  £{transactionList
                                    .reduce((sum, t) => sum + t.amount, 0)
                                    .toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Tab 3: Workflow */}
                <TabsContent value="workflow" className="space-y-4">
                  {(() => {
                    // Get the workflow configuration for this dispute type
                    const workflowConfig = getWorkflowForDisputeType(selectedDispute.type);
                    const workflowStages = workflowConfig.stages;
                    
                    // Generate completion dates based on current level
                    // First stage completed on created date, subsequent stages 1 day apart
                    const baseDate = new Date(selectedDispute.createdDate);
                    const stagesWithDates = workflowStages.map((stage, index) => ({
                      ...stage,
                      completedDate: stage.level <= selectedDispute.currentLevel 
                        ? new Date(baseDate.getTime() + index * 24 * 60 * 60 * 1000).toISOString()
                        : null,
                    }));

                    return (
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-lg">Active Workflow</CardTitle>
                          <CardDescription>{workflowConfig.workflowName}</CardDescription>
                        </CardHeader>
                        <CardContent>
                          <div className="flex items-center gap-2 mb-6">
                            <span className="text-sm font-medium">Current Level:</span>
                            <Badge>{selectedDispute.currentLevel} of {workflowStages.length}</Badge>
                          </div>

                          <div className="space-y-6">
                            {stagesWithDates.map((step, index) => (
                              <div key={step.level} className="flex gap-4">
                                <div className="flex flex-col items-center">
                                  <div className={`flex h-8 w-8 items-center justify-center rounded-full ${
                                    step.level <= selectedDispute.currentLevel
                                      ? "bg-primary text-primary-foreground"
                                      : "bg-muted text-muted-foreground"
                                  }`}>
                                    {step.level < selectedDispute.currentLevel ? (
                                      <CheckCircle2 className="h-4 w-4" />
                                    ) : step.level === selectedDispute.currentLevel ? (
                                      <Clock className="h-4 w-4" />
                                    ) : (
                                      <span className="text-sm">{step.level}</span>
                                    )}
                                  </div>
                                  {index < workflowStages.length - 1 && (
                                    <div className={`w-0.5 h-12 ${
                                      step.level < selectedDispute.currentLevel
                                        ? "bg-primary"
                                        : "bg-muted"
                                    }`} />
                                  )}
                                </div>
                                <div className="flex-1 pb-8">
                                  <h4 className="font-semibold">{step.title}</h4>
                                  <p className="text-sm text-muted-foreground mt-1">
                                    {step.description}
                                  </p>
                                  {step.completedDate && (
                                    <p className="text-xs text-muted-foreground mt-2">
                                      Completed: {format(new Date(step.completedDate), "MMM dd, yyyy")}
                                    </p>
                                  )}
                                  {Boolean(step.estimatedDays) && !step.completedDate && (
                                    <p className="text-xs text-muted-foreground mt-2">
                                      Estimated: {step.estimatedDays} day{step.estimatedDays !== 1 ? 's' : ''}
                                    </p>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })()}
                </TabsContent>

                {/* Tab 4: Activity */}
                <TabsContent value="activity" className="space-y-4">
                  <ConsolidatedActivityTimeline
                    activities={mockActivities}
                    communications={mockCommunications}
                    notes={mockNotes}
                    isEditMode={isEditMode}
                    onAddNote={async (content: string) => {
                      // Handle adding note - in a real implementation, this would call an API
                      console.log("Adding note:", content);
                      // You can add toast notification here
                    }}
                  />
                </TabsContent>

                {/* Tab 5: Documents */}
                <TabsContent value="documents" className="space-y-4">
                  <Button className="w-full">
                    <FileText className="mr-2 h-4 w-4" />
                    Upload Document
                  </Button>
                  <div className="space-y-3">
                    {documents.map((doc, index) => (
                      <Card key={index}>
                        <CardContent className="pt-6">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <FileText className="h-8 w-8 text-muted-foreground" />
                              <div>
                                <p className="font-medium text-sm">{doc.name}</p>
                                <p className="text-xs text-muted-foreground">
                                  {doc.size} • Uploaded {format(new Date(doc.date), "MMM dd, yyyy")}
                                </p>
                              </div>
                            </div>
                            <Button variant="ghost" size="sm">
                              <Eye className="h-4 w-4" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </TabsContent>

                {/* Tab 6: AI Insights */}
                <TabsContent value="ai-insights" className="space-y-4">
                  <Card className="border-purple-200 dark:border-purple-800">
                    <CardHeader>
                      <div className="flex items-center gap-2">
                        <Sparkles className="h-5 w-5 text-purple-500" />
                        <CardTitle className="text-lg">AI-Powered Insights</CardTitle>
                      </div>
                      <CardDescription>
                        Automated analysis by Dispute Resolute Agent
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      {/* Confidence Score */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label>Confidence Score</Label>
                          <span className="text-sm font-medium">{selectedDispute.aiScore}%</span>
                        </div>
                        <Progress value={selectedDispute.aiScore} className="h-2" />
                      </div>

                      {/* AI Recommendation */}
                      <div className="space-y-2">
                        <Label>AI Recommendation</Label>
                        <Card className="bg-purple-50 dark:bg-purple-950 border-purple-200 dark:border-purple-800">
                          <CardContent className="pt-4">
                            <p className="text-sm">{selectedDispute.aiRecommendation}</p>
                          </CardContent>
                        </Card>
                      </div>

                      {/* Similar Cases */}
                      <div className="space-y-2">
                        <Label>Similar Cases Analysis</Label>
                        <p className="text-sm text-muted-foreground">
                          Based on analysis of 47 similar disputes, 82% were resolved with partial credit.
                          Average resolution time: 12 days.
                        </p>
                      </div>

                      {/* Risk Assessment */}
                      <div className="space-y-2">
                        <Label>Risk Assessment</Label>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-amber-600 border-amber-600">
                            Medium Risk
                          </Badge>
                          <span className="text-sm text-muted-foreground">
                            Customer has good payment history
                          </span>
                        </div>
                      </div>

                      {/* Suggested Actions */}
                      <div className="space-y-2">
                        <Label>Suggested Actions</Label>
                        <ul className="mt-2 space-y-2">
                          <li className="flex items-start gap-2 text-sm">
                            <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5" />
                            <span>Verify service delivery dates from system logs</span>
                          </li>
                          <li className="flex items-start gap-2 text-sm">
                            <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5" />
                            <span>Review contract terms for billing cycle</span>
                          </li>
                          <li className="flex items-start gap-2 text-sm">
                            <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5" />
                            <span>Consider 30% credit as resolution</span>
                          </li>
                        </ul>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>


            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Create Dispute Drawer */}
      <Sheet open={isCreateDrawerOpen} onOpenChange={(open) => {
        setIsCreateDrawerOpen(open)
        if (!open) {
          setCreateForm(initialCreateFormState)
          setCreateFormContacts([])
        }
      }}>
        <SheetContent 
          side="right" 
          className="w-full sm:max-w-2xl overflow-y-auto"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <SheetHeader>
            <div className="flex items-start justify-between">
              <div className="space-y-1">
                <SheetTitle className="text-2xl flex items-center gap-2">
                  <Plus className="h-6 w-6 text-blue-600" />
                  Create New Dispute
                </SheetTitle>
                <SheetDescription>
                  Enter dispute details to create a new case
                </SheetDescription>
              </div>
            </div>
          </SheetHeader>

          <Tabs defaultValue="overview" className="mt-6">
            <TabsList className="grid w-full grid-cols-6">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="transactions">Transactions</TabsTrigger>
              <TabsTrigger value="workflow">Workflow</TabsTrigger>
              <TabsTrigger value="activity">Activity</TabsTrigger>
              <TabsTrigger value="documents">Documents</TabsTrigger>
              <TabsTrigger value="ai-insights">AI Insights</TabsTrigger>
            </TabsList>

            {/* Tab 1: Overview */}
            <TabsContent value="overview" className="space-y-3 mt-4">
              {/* Top Row: Customer Information and Dispute Summary */}
              <div className="grid gap-3 md:grid-cols-2">
                {/* Customer Information Panel */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <User className="h-4 w-4 text-primary" />
                      Customer Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="space-y-2">
                      <Label htmlFor="customer">Customer</Label>
                      <Select value={createForm.customerId} onValueChange={(value) => handleCreateFormChange("customerId", value)}>
                        <SelectTrigger id="customer">
                          <SelectValue placeholder="Select customer" />
                        </SelectTrigger>
                        <SelectContent>
                          {customerOptions.map((customer) => (
                            <SelectItem key={customer.id} value={String(customer.id)}>
                              {customer.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <Separator />

                    <div className="space-y-2">
                      <Label htmlFor="contact">Contact</Label>
                      <Select 
                        value={createForm.contactId} 
                        onValueChange={(value) => handleCreateFormChange("contactId", value)}
                        disabled={!createForm.customerId || createFormContacts.length === 0}
                      >
                        <SelectTrigger id="contact">
                          <SelectValue placeholder={getContactPlaceholder(createForm.customerId, createFormContacts.length)} />
                        </SelectTrigger>
                        <SelectContent>
                          {createFormContacts.map((contact) => (
                            <SelectItem key={contact.id} value={contact.id}>
                              {contact.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="contact-email" className="flex items-center gap-2">
                        <Mail className="h-3 w-3 text-muted-foreground" />
                        Email
                      </Label>
                      <Input 
                        id="contact-email" 
                        value={createForm.contactEmail} 
                        readOnly
                        placeholder="Auto-populated from contact"
                        className="bg-muted"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="contact-phone" className="flex items-center gap-2">
                        <Phone className="h-3 w-3 text-muted-foreground" />
                        Phone
                      </Label>
                      <Input 
                        id="contact-phone" 
                        value={createForm.contactPhone} 
                        readOnly
                        placeholder="Auto-populated from contact"
                        className="bg-muted"
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Dispute Summary Panel */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-orange-600 dark:text-orange-400" />
                      Dispute Summary
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="type">Dispute Type</Label>
                      <Select value={createForm.disputeTypeId} onValueChange={(value) => handleCreateFormChange("disputeTypeId", value)}>
                        <SelectTrigger id="type">
                          <SelectValue placeholder="Select dispute type" />
                        </SelectTrigger>
                        <SelectContent>
                          {disputeTypeOptions.map((type) => (
                            <SelectItem key={type.id} value={String(type.id)}>
                              {type.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="amount">Disputed Amount</Label>
                      <Input 
                        id="amount" 
                        type="number" 
                        placeholder="0.00"
                        value={createForm.amount}
                        onChange={(e) => handleCreateFormChange("amount", e.target.value)}
                      />
                    </div>
                  </div>

                  <Separator />

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="priority">Priority</Label>
                      <Select value={createForm.priorityId} onValueChange={(value) => handleCreateFormChange("priorityId", value)}>
                        <SelectTrigger id="priority">
                          <SelectValue placeholder="Select priority" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">Critical</SelectItem>
                          <SelectItem value="2">High</SelectItem>
                          <SelectItem value="3">Medium</SelectItem>
                          <SelectItem value="4">Low</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="due-date">Due Date</Label>
                      <Input 
                        id="due-date" 
                        type="date"
                        value={createForm.dueDate}
                        onChange={(e) => handleCreateFormChange("dueDate", e.target.value)}
                      />
                    </div>
                  </div>

                  <Separator />

                  <div className="space-y-2">
                    <Label htmlFor="assigned-to">Assigned To</Label>
                    <Select value={createForm.assignedToUserId} onValueChange={(value) => handleCreateFormChange("assignedToUserId", value)}>
                      <SelectTrigger id="assigned-to">
                        <SelectValue placeholder="Select user (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        {userOptions.map((user) => (
                          <SelectItem key={user.id} value={String(user.id)}>
                            {user.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  </CardContent>
                </Card>
              </div>

              {/* Description */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <MessageSquare className="h-4 w-4 text-green-600 dark:text-green-400" />
                    Description
                  </CardTitle>
                  <CardDescription>
                    Describe the dispute in detail
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Textarea
                    id="description"
                    placeholder="Enter dispute description..."
                    rows={6}
                    value={createForm.description}
                    onChange={(e) => handleCreateFormChange("description", e.target.value)}
                  />
                </CardContent>
              </Card>

              {/* AI Recommendation */}
              <Card className="border-purple-200 bg-purple-50/50 dark:border-purple-800 dark:bg-purple-950/20">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                    AI-Powered Processing
                  </CardTitle>
                  <CardDescription>
                    Automated dispute analysis
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    This dispute will be automatically categorized, prioritized, and routed to the
                    appropriate workflow by the Dispute Resolute Agent.
                  </p>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Tab 2: Transactions */}
            <TabsContent value="transactions" className="space-y-4 mt-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Receipt className="h-4 w-4 text-primary" />
                    Transactions Under Dispute
                  </CardTitle>
                  <CardDescription>
                    Add transactions associated with this dispute
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <Button
                      size="sm"
                      onClick={() => {
                        // Add transaction logic
                        console.log("Add transaction");
                      }}
                      className="gap-2"
                    >
                      <Plus className="h-4 w-4" />
                      Add Transaction
                    </Button>
                    
                    <div className="text-center py-8 text-muted-foreground">
                      <Receipt className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p className="font-medium">No transactions added yet</p>
                      <p className="text-sm mt-2">Click "Add Transaction" to add a transaction to this dispute</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Tab 3: Workflow */}
            <TabsContent value="workflow" className="space-y-4 mt-4">
              {(() => {
                // Get the selected dispute type to show its workflow
                const selectedDisputeTypeOption = disputeTypeOptions.find(
                  dt => String(dt.id) === createForm.disputeTypeId
                );
                const disputeTypeName = selectedDisputeTypeOption?.name || '';
                
                // Get workflow configuration for the selected dispute type
                const workflowConfig = disputeTypeName 
                  ? getWorkflowForDisputeType(disputeTypeName)
                  : null;

                return (
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Activity className="h-4 w-4 text-primary" />
                        Workflow Preview
                      </CardTitle>
                      <CardDescription>
                        {workflowConfig 
                          ? `This dispute will follow the ${workflowConfig.workflowName}`
                          : 'Select a dispute type to see the workflow stages'}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {!workflowConfig ? (
                        <div className="text-center py-8 text-muted-foreground">
                          <GitBranch className="h-12 w-12 mx-auto mb-3 opacity-50" />
                          <p className="text-sm">Please select a dispute type in the Overview tab to view the workflow stages</p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          <div>
                            <div className="text-sm text-muted-foreground mb-2">
                              Workflow Stages ({workflowConfig.stages.length} level{workflowConfig.stages.length !== 1 ? 's' : ''})
                            </div>
                          </div>
                          <div className="space-y-6">
                            {workflowConfig.stages.map((step, index) => (
                              <div key={step.level} className="flex gap-4">
                                <div className="flex flex-col items-center">
                                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted text-muted-foreground">
                                    <span className="text-sm">{step.level}</span>
                                  </div>
                                  {index < workflowConfig.stages.length - 1 && (
                                    <div className="w-0.5 h-12 bg-muted" />
                                  )}
                                </div>
                                <div className="flex-1 pb-8">
                                  <h4 className="font-semibold">{step.title}</h4>
                                  <p className="text-sm text-muted-foreground mt-1">
                                    {step.description}
                                  </p>
                                  {Boolean(step.estimatedDays) && (
                                    <p className="text-xs text-muted-foreground mt-2">
                                      <Clock className="h-3 w-3 inline mr-1" />
                                      Estimated: {step.estimatedDays} day{step.estimatedDays !== 1 ? 's' : ''}
                                    </p>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })()}
            </TabsContent>

            {/* Tab 4: Activity */}
            <TabsContent value="activity" className="space-y-4 mt-4">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <MessageSquare className="h-4 w-4 text-green-600 dark:text-green-400" />
                    Initial Note
                  </CardTitle>
                  <CardDescription>Add any initial notes about this dispute</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="initial-note">Initial Note</Label>
                    <Textarea
                      id="initial-note"
                      placeholder="Add any relevant notes about this dispute..."
                      rows={6}
                    />
                  </div>

                  <Separator />

                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Checkbox id="notify-customer" />
                      <Label htmlFor="notify-customer" className="text-sm font-normal cursor-pointer">
                        Send notification to customer about dispute creation
                      </Label>
                    </div>

                    <div className="flex items-center gap-2">
                      <Checkbox id="notify-assignee" />
                      <Label htmlFor="notify-assignee" className="text-sm font-normal cursor-pointer">
                        Notify assigned team member
                      </Label>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Activity className="h-4 w-4 text-primary" />
                    Activity Timeline
                  </CardTitle>
                  <CardDescription>
                    Activity history will be available after the dispute is created
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-8 text-muted-foreground">
                    <Activity className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p className="text-sm">No activities recorded yet</p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Tab 5: Documents */}
            <TabsContent value="documents" className="space-y-4 mt-4">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <FileText className="h-4 w-4 text-primary" />
                    Supporting Documents
                  </CardTitle>
                  <CardDescription>Upload any relevant documents for this dispute</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-8 text-center">
                    <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-sm font-medium mb-1">Drop files here or click to upload</p>
                    <p className="text-xs text-muted-foreground mb-4">
                      Supported formats: PDF, DOC, DOCX, XLS, XLSX, JPG, PNG (Max 10MB)
                    </p>
                    <Button variant="outline" size="sm">
                      <FileText className="mr-2 h-4 w-4" />
                      Browse Files
                    </Button>
                  </div>

                  <Separator />

                  <div className="space-y-2">
                    <Label>Document Types</Label>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="flex items-center gap-2">
                        <Checkbox id="doc-invoice" />
                        <Label htmlFor="doc-invoice" className="text-sm font-normal cursor-pointer">
                          Invoice
                        </Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox id="doc-contract" />
                        <Label htmlFor="doc-contract" className="text-sm font-normal cursor-pointer">
                          Contract
                        </Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox id="doc-email" />
                        <Label htmlFor="doc-email" className="text-sm font-normal cursor-pointer">
                          Email Correspondence
                        </Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox id="doc-other" />
                        <Label htmlFor="doc-other" className="text-sm font-normal cursor-pointer">
                          Other
                        </Label>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Tab 6: AI Insights */}
            <TabsContent value="ai-insights" className="space-y-4 mt-4">
              <Card className="border-purple-200 bg-purple-50/50 dark:border-purple-800 dark:bg-purple-950/20">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                    AI-Powered Analysis
                  </CardTitle>
                  <CardDescription>
                    Preview of AI capabilities for dispute resolution
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="space-y-2">
                    <div className="text-sm font-medium">Automated Categorization</div>
                    <p className="text-sm text-muted-foreground">
                      The AI agent will automatically analyse the dispute description and categorise it
                      based on historical patterns and similar cases.
                    </p>
                  </div>

                  <Separator />

                  <div className="space-y-2">
                    <div className="text-sm font-medium">Priority Assessment</div>
                    <p className="text-sm text-muted-foreground">
                      AI will evaluate the dispute amount, customer history, and urgency to recommend
                      an appropriate priority level.
                    </p>
                  </div>

                  <Separator />

                  <div className="space-y-2">
                    <div className="text-sm font-medium">Similar Cases</div>
                    <p className="text-sm text-muted-foreground">
                      Once created, the system will analyse similar historical disputes to provide
                      resolution recommendations and estimated timelines.
                    </p>
                  </div>

                  <Separator />

                  <div className="space-y-2">
                    <div className="text-sm font-medium">Workflow Routing</div>
                    <p className="text-sm text-muted-foreground">
                      The dispute will be automatically routed to the most appropriate workflow based
                      on its characteristics and complexity.
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-blue-200 bg-blue-50/50 dark:border-blue-800 dark:bg-blue-950/20">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                    AI Analysis Available After Creation
                  </CardTitle>
                  <CardDescription>
                    Detailed insights will be generated after dispute creation
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Detailed AI insights and recommendations will be generated once the dispute
                    is created and processed by the system.
                  </p>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          <div className="mt-6 flex justify-end gap-2">
            <Button variant="outline" onClick={() => setIsCreateDrawerOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateDispute}
              disabled={createDisputeMutation.isPending || !createForm.customerId || !createForm.disputeTypeId || !createForm.amount || !createForm.priorityId}
            >
              {createDisputeMutation.isPending ? "Creating..." : "Create Dispute"}
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Floating Actions Panel */}
      {selectedDisputeId && selectedDispute && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 animate-in slide-in-from-bottom-5 duration-300">
          <div className="bg-primary text-primary-foreground rounded-lg shadow-2xl border border-primary/20">
            <div className="flex items-center gap-4 px-6 py-4">
              {/* Selection Info */}
              <div className="flex items-center gap-3 border-r border-primary-foreground/20 pr-4">
                <div className="flex flex-col">
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="bg-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground/30">
                      1
                    </Badge>
                    <span className="font-medium">dispute selected</span>
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-sm text-primary-foreground/80">
                    <span className="font-semibold">{selectedDispute.id}</span>
                    <span>•</span>
                    <span>{selectedDispute.customer}</span>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setLogCallOpen(true)}
                  className="gap-2"
                >
                  <Phone className="h-4 w-4" />
                  Log Call
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setSendEmailOpen(true)}
                  className="gap-2"
                >
                  <Mail className="h-4 w-4" />
                  Send Email
                </Button>

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSelectedDisputeId(null)
                    setSelectedDispute(null)
                  }}
                  className="gap-2 hover:bg-primary-foreground/20"
                >
                  <X className="h-4 w-4" />
                  Clear
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Log Call Modal */}
      <LogCallModal
        open={logCallOpen}
        onOpenChange={setLogCallOpen}
        customerName={selectedDispute?.customer || ""}
        customerPhone={selectedDispute?.contactPhone}
        disputeId={selectedDisputeNumericId ? selectedDisputeNumericId.toString() : undefined}
      />

      {/* Send Email Modal */}
      <SendEmailModal
        open={sendEmailOpen}
        onOpenChange={setSendEmailOpen}
        customerName={selectedDispute?.customer || ""}
        customerEmail={selectedDispute?.contactEmail || ""}
        customerId={selectedDispute?.customerId?.toString()}
        sourceType="dispute"
        sourceId={selectedDisputeNumericId ? selectedDisputeNumericId.toString() : undefined}
      />

      {/* Next Action Modal */}
      <Dialog open={isNextActionModalOpen} onOpenChange={setIsNextActionModalOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-primary" />
              Proceed to Next Stage
            </DialogTitle>
            <DialogDescription>
              Complete the current stage and advance the dispute workflow
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Next Stage Info */}
            {selectedDispute && (() => {
              const workflowConfig = getWorkflowForDisputeType(selectedDispute.type);
              const nextStage = workflowConfig.stages.find(s => s.level === selectedDispute.currentLevel + 1);
              
              return nextStage ? (
                <div className="rounded-lg bg-blue-50 dark:bg-blue-950 p-4 space-y-2">
                  <div className="flex items-center gap-2">
                    <Zap className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                    <p className="font-semibold text-sm text-blue-900 dark:text-blue-100">
                      {nextStage.title}
                    </p>
                  </div>
                  <p className="text-xs text-blue-700 dark:text-blue-300">
                    {nextStage.description}
                  </p>
                  {Boolean(nextStage.estimatedDays) && (
                    <p className="text-xs text-blue-600 dark:text-blue-400">
                      Estimated time: {nextStage.estimatedDays} day{nextStage.estimatedDays !== 1 ? 's' : ''}
                    </p>
                  )}
                </div>
              ) : null;
            })()}

            {/* Action Notes */}
            <div className="space-y-2">
              <Label htmlFor="action-notes" className="flex items-center gap-1">
                Action Notes{" "}
                <span className="text-destructive">*</span>
              </Label>
              <Textarea
                id="action-notes"
                placeholder="Enter notes about the actions taken and any relevant details..."
                value={nextActionNotes}
                onChange={(e) => setNextActionNotes(e.target.value)}
                rows={4}
                className="resize-none"
              />
              <p className="text-xs text-muted-foreground">
                These notes will be added to the activity timeline
              </p>
            </div>

            {/* Follow-up Date */}
            <div className="space-y-2">
              <Label htmlFor="follow-up-date">Follow-up Date (Optional)</Label>
              <Input
                id="follow-up-date"
                type="date"
                value={nextActionFollowUpDate}
                onChange={(e) => setNextActionFollowUpDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
              />
              <p className="text-xs text-muted-foreground">
                Set a reminder for the next action
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleCancelNextAction}
            >
              Cancel
            </Button>
            <Button
              onClick={handleProceedToNextStage}
              disabled={!nextActionNotes.trim()}
              className="gap-2"
            >
              <CheckCircle2 className="h-4 w-4" />
              Proceed
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
